import { useState } from 'react';
import { Alert, Box, Chip, LinearProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { StepProps } from '../stepProps';
import ConditionBuilder, { IssueList, MONO, describeFilter, entityNoun, issuesAt } from '../ConditionBuilder';

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export default function FilterStep({ pipeline, spec, onChange, catalogue, lookups }: StepProps) {
  const [list, setList] = useState<'matched' | 'excluded'>('matched');
  const [one, many] = entityNoun(spec.trigger.entity);
  const filter = spec.filter ?? { kind: 'group' as const, op: 'AND' as const, children: [] };
  const t = pipeline.lastTest;
  // Nothing fancy: the draft was saved after the test ran, so the numbers may be out of date.
  const stale = !!t && Date.parse(pipeline.updatedAt) - Date.parse(t.ranAt) > 5000;
  const pct = t && t.total ? (t.matched / t.total) * 100 : 0;
  const tracked = t ? (t.plans?.FLAG ?? 0) + (t.plans?.ADOPT ?? 0) : 0;
  const fresh = t ? t.plans?.CREATE ?? 0 : 0;

  return (
    <Stack spacing={2.25}>
      <Typography variant="h5">Which {many} should become transactions?</Typography>

      <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: 'text.primary', maxWidth: '72ch' }}>
        {describeFilter(filter, many, lookups)}
      </Typography>

      <IssueList issues={issuesAt(pipeline.draftValidation, ['filter'])} />

      <ConditionBuilder group={filter} onChange={(g) => onChange({ ...spec, filter: g })} catalogue={catalogue} lookups={lookups} />

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, display: 'grid', gap: 1.25 }}>
        {!t ? (
          <Typography variant="body2" color="text.secondary">
            Not tested yet. Run the test on step 6 to see how many {many} this filter matches.
          </Typography>
        ) : (
          <>
            {stale && (
              <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }}>
                The pipeline changed after this test. Run the test to refresh these numbers.
              </Alert>
            )}
            <Stack direction="row" alignItems="baseline" spacing={0.75} flexWrap="wrap">
              <Typography sx={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {t.matched.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                of {t.total.toLocaleString()} {many} match
              </Typography>
              <Typography variant="caption" color="text.secondary">
                · last {t.days} days · about {Math.round(t.matched / Math.max(1, t.days))} a day · {t.excluded.toLocaleString()} excluded ·
                tested {fmtDate(t.ranAt)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(pct, t.matched ? 1 : 0))}
              aria-label={`${t.matched} of ${t.total} ${many} match, ${pct.toFixed(1)} percent`}
              sx={{ height: 8, borderRadius: 999 }}
            />
            {(tracked > 0 || fresh > 0) && (
              <Stack direction="row" spacing={2.25} flexWrap="wrap" sx={{ fontSize: 13 }}>
                {tracked > 0 && (
                  <span>
                    <Box component="span" sx={{ color: 'info.main' }}>
                      ●
                    </Box>{' '}
                    {tracked.toLocaleString()} are already tracked in QuikStep
                  </span>
                )}
                {fresh > 0 && (
                  <span>
                    <Box component="span" sx={{ color: 'success.main' }}>
                      ●
                    </Box>{' '}
                    {fresh.toLocaleString()} are new
                  </span>
                )}
              </Stack>
            )}

            <ToggleButtonGroup size="small" exclusive value={list} onChange={(_, v) => v && setList(v)} aria-label="Show">
              <ToggleButton value="matched" sx={{ textTransform: 'none', py: 0.25, px: 1.5 }}>
                Matched
              </ToggleButton>
              <ToggleButton value="excluded" sx={{ textTransform: 'none', py: 0.25, px: 1.5 }}>
                Excluded
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '9px', overflow: 'hidden' }}>
              {(list === 'matched' ? t.sampleMatched ?? [] : t.sampleExcluded ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.25 }}>
                  No {list} {many} in the test sample.
                </Typography>
              ) : list === 'matched' ? (
                (t.sampleMatched ?? []).map((r) => (
                  <Row key={r.recordId} id={r.recordId} text={r.note ? `${r.summary} · ${r.note}` : r.summary} tag={r.plan} />
                ))
              ) : (
                (t.sampleExcluded ?? []).map((r) => <Row key={r.recordId} id={r.recordId} text={r.reason} tag="Excluded" />)
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Each {one} in the test was checked against this filter; the lists show a sample.
            </Typography>
          </>
        )}
      </Box>
    </Stack>
  );
}

function Row({ id, text, tag }: { id: string; text: string; tag: string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '72px minmax(0,1fr) auto',
        gap: 1.25,
        px: 1.5,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        fontSize: 13,
        alignItems: 'center',
        '&:last-of-type': { borderBottom: 0 }
      }}
    >
      <Box component="code" sx={{ fontFamily: MONO, fontSize: 12.5 }}>
        {id}
      </Box>
      <Typography variant="body2" noWrap title={text}>
        {text}
      </Typography>
      <Chip size="small" label={tag} />
    </Box>
  );
}
